import CirclePattern from "./CirclePattern";
import PTCTechnology from "./PTCTechnology";
import { Check, Download, ArrowUpRight } from "lucide-react";

const TECH_SPEC_PDF = "/ecoheatwave-technical-specification.pdf";

const MODERN_BULLETS = [
  "Even, balanced heat distribution",
  "Invisible integration beneath finished surfaces",
  "No moving parts or mechanical complexity",
  "Fast, efficient installation",
  "Compatibility with modern renewable energy systems",
];

const FUTURE_BULLETS = [
  "Solar PV systems",
  "Battery storage",
  "Smart energy controls",
  "Low-carbon hot water systems",
];

export default function Product() {
  return (
    <section id="product" data-testid="product-section" className="mt-[10mm] py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <IntroSection />
        <PTCTechnology />
        <div className="my-[14mm]" />
        <ModernBuildingsSection />
        <div className="my-[14mm]" />
        <FutureEnergySection />
        <FHSHEMSection />
        <TechSpecCard />
        <div className="h-[30mm]" />
      </div>
      <ProvenPrinciplesBanner />
    </section>
  );
}

function IntroSection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-7">
        <div className="overline mb-6">Technology</div>
        <h2
          data-testid="product-headline"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-ecoh-ink"
        >
          Why thin film carbon heating?
        </h2>
        <p className="mt-6 font-display text-xl md:text-2xl font-light tracking-tight text-ecoh-ink/75 leading-snug">
          The modern evolution of radiant warmth.
        </p>
        <div className="mt-10 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
          <p>
            Radiant heating has long been recognised as one of the most
            comfortable and efficient ways to heat a space. By warming surfaces
            and occupants directly, heat is absorbed into the fabric of the
            building and released gradually, creating a stable indoor
            temperature, improving thermal comfort, and enhancing energy
            efficiency.
          </p>
          <p>
            ecoHeatwave builds on this proven principle using advanced thin film
            carbon technology, delivering clean, even radiant warmth through an
            ultra-thin heating layer installed behind the plasterboard ceiling.
          </p>
          <p>
            The result is a heating system that combines the comfort benefits of
            traditional radiant heat with the performance, flexibility, and
            simplicity required for modern construction.
          </p>
        </div>
      </div>
      <ProductRollsImage />
    </div>
  );
}

function ProductRollsImage() {
  return (
    <div className="lg:col-span-5 lg:sticky lg:top-28">
      <div className="relative rounded-2xl overflow-hidden bg-ecoh-surface aspect-square">
        <img
          src="https://customer-assets.emergentagent.com/job_thermal-design-3/artifacts/ha1toed7_ECOHEATWAVE-5.jpeg"
          alt="Three rolls of ecoHeatwave thin film carbon heating on a UK construction site"
          data-testid="product-rolls-image"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
        />
        <div
          data-testid="made-in-britain-badge"
          className="absolute bottom-4 right-4 md:bottom-6 md:right-6 bg-white rounded-md shadow-lg p-2 md:p-2.5 ring-1 ring-black/5"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_thermal-design-3/artifacts/wnev4dnm_image.png"
            alt="Made in Britain"
            className="block w-20 md:w-24 h-auto"
          />
        </div>
      </div>
      <p className="mt-3 text-xs tracking-[0.2em] uppercase text-ecoh-muted">
        All ecoHeatwave heating systems are designed and manufactured in our state-of-the-art factory, located in East Yorkshire
      </p>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-3 pt-1">
      {items.map((b) => (
        <li key={b} className="flex items-start gap-3">
          <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 bg-ecoh-accent text-white">
            <Check className="w-3 h-3" strokeWidth={3} />
          </span>
          <span className="text-ecoh-ink/85 text-[15px] md:text-base">{b}</span>
        </li>
      ))}
    </ul>
  );
}

function ModernBuildingsSection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <h3
          data-testid="product-modern-headline"
          className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.08] text-ecoh-ink"
        >
          Designed for modern buildings.
        </h3>
      </div>
      <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        <p>
          Unlike conventional heating systems that rely on bulky mechanical
          components, thin film carbon heating uses advanced conductive layers to
          create consistent radiant warmth across the installed surface.
        </p>
        <p>This allows ecoHeatwave to deliver:</p>
        <BulletList items={MODERN_BULLETS} />
        <p className="pt-2 font-medium text-ecoh-ink">
          By removing unnecessary system complexity, ecoHeatwave offers a simpler,
          more elegant approach to delivering comfort in modern spaces.
        </p>
      </div>
    </div>
  );
}

function FutureEnergySection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <h3
          data-testid="product-future-headline"
          className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.08] text-ecoh-ink"
        >
          Built for the future of energy.
        </h3>
      </div>
      <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
        <p>
          As homes and buildings move towards electrification, renewable
          integration, and lower-carbon performance, heating systems need to work
          more intelligently within the wider energy ecosystem.
        </p>
        <p>Thin film carbon heating is naturally suited to this shift—working seamlessly with:</p>
        <BulletList items={FUTURE_BULLETS} />
        <p className="pt-2 font-medium text-ecoh-ink">
          This makes ecoHeatwave more than a heating system—it becomes part of a
          connected, energy-aware building design.
        </p>
      </div>
    </div>
  );
}

function FHSHEMHeadline() {
  return (
    <div className="lg:col-span-5">
      <div className="overline mb-6">Compliance</div>
      <h3
        data-testid="fhs-hem-headline"
        className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.08] text-ecoh-ink"
      >
        Future Home Standard, Home Energy Model
        <br />
        <span className="text-ecoh-accent">&amp; New Build Heat Standard ready.</span>
      </h3>
      <div className="mt-10 rounded-2xl bg-ecoh-surface p-7 md:p-8">
        <div className="text-[11px] tracking-[0.22em] uppercase text-ecoh-accent">
          SAP 10.3 verified
        </div>
        <p className="mt-3 font-display text-2xl md:text-3xl font-light tracking-tight text-ecoh-ink leading-snug">
          EPC A ratings demonstrated
          <br />
          in new-build homes.
        </p>
      </div>
    </div>
  );
}

function FHSHEMBody() {
  return (
    <div className="lg:col-span-7 space-y-6 text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
      <p>
        ecoHeatwave is more than just a heating system — it is part of a
        complete low-carbon home energy solution. When combined with an
        air-source hot water cylinder, solar PV, battery storage and
        intelligent room-by-room controls, ecoHeatwave has demonstrated
        exceptional performance in SAP 10.3 assessments, achieving EPC A
        ratings in new-build homes.
      </p>
      <p>
        Unlike traditional heating systems that rely on radiators, pipework
        and plant rooms, ecoHeatwave&apos;s invisible radiant heating
        technology is integrated within the fabric of the building,
        maximising living space while delivering comfortable, even warmth
        throughout the home.
      </p>
      <p>
        By combining efficient electric heating, renewable energy generation
        and smart energy storage, ecoHeatwave helps homeowners reduce grid
        dependency, make better use of off-peak electricity and support the
        transition to a smarter, more flexible energy future.
      </p>
      <p className="pt-2 font-medium text-ecoh-ink">
        Designed for modern homes and aligned with the evolving demands of
        energy efficiency standards, ecoHeatwave offers developers, housing
        providers and homeowners a simple, elegant and future-ready approach
        to low-carbon heating.
      </p>
    </div>
  );
}

function FHSHEMSection() {
  return (
    <div className="mt-[30mm]" data-testid="fhs-hem-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <FHSHEMHeadline />
        <FHSHEMBody />
      </div>
    </div>
  );
}

function TechSpecCard() {
  return (
    <div className="mt-[30mm]" data-testid="tech-spec-card">
      <div className="rounded-3xl bg-white ring-1 ring-ecoh-ink/10 shadow-sm p-8 md:p-12 lg:p-14">
        <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
          Technical Specification.
        </h3>
        <p className="mt-5 max-w-2xl text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
          Full system dimensions, electrical and thermal performance,
          compliance, certification and provenance — everything you
          need to specify ecoHeatwave on your project.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={TECH_SPEC_PDF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tech-spec-view"
            className="inline-flex items-center gap-2 rounded-full bg-ecoh-ink text-white px-6 py-3 text-[13px] font-medium hover:bg-ecoh-accent transition-colors duration-300"
          >
            View PDF
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </a>
          <a
            href={TECH_SPEC_PDF}
            download
            data-testid="tech-spec-download"
            className="inline-flex items-center gap-2 rounded-full ring-1 ring-ecoh-ink/15 text-ecoh-ink px-6 py-3 text-[13px] font-medium hover:bg-ecoh-ink/[0.04] transition-colors duration-300"
          >
            <Download className="w-4 h-4" strokeWidth={2} />
            Download
          </a>
        </div>
      </div>
    </div>
  );
}

function ProvenPrinciplesBanner() {
  return (
    <div className="relative bg-ecoh-ink text-white overflow-hidden">
      <CirclePattern
        className="pointer-events-none select-none absolute inset-0 w-full h-full"
        opacity={0.55}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ecoh-ink via-ecoh-ink/85 to-ecoh-ink/30" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-8 lg:col-start-1 text-left">
            <h3
              data-testid="product-proven-headline"
              className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-[-0.02em] leading-[1.08] text-white"
            >
              Proven principles.
              <br />
              <span className="font-extralight text-ecoh-amber">Modern performance.</span>
            </h3>
            <p className="mt-8 max-w-2xl text-white/75 text-base md:text-lg font-light leading-relaxed">
              Thin film carbon heating combines the proven comfort of radiant
              warmth with the simplicity of solid-state technology—creating a
              low-maintenance, energy-efficient system designed for the demands of
              modern construction.
            </p>
            <p className="mt-12 max-w-2xl font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-white leading-snug">
              Invisible by design. Intelligent by nature.
              <br />
              <span className="text-ecoh-amber">Built for the future.</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
